export default function MarketingSection() {
    return (
        <section className="py-24 border-t border-white/5">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div>
                    <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent mb-6">
                        App Store Ready Icons. <br />
                        <span className="text-violet-400">Perfectly Sized.</span>
                    </h2>
                    <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                        Stop worrying about export dimensions and transparency requirements. AuraIcon generates
                        icons that meet strict Apple and Google Play guidelines, out of the box.
                    </p>

                    <div className="space-y-6">
                        <div className="flex gap-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-400 border border-violet-500/20">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-white font-semibold mb-1">iOS & Android Optimization</h3>
                                <p className="text-slate-400 text-sm">Automated safe-zone margins and correct padding for mobile OS requirements.</p>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-fuchsia-500/10 flex items-center justify-center text-fuchsia-400 border border-fuchsia-500/20">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-white font-semibold mb-1">High-Resolution SVG & WebP</h3>
                                <p className="text-slate-400 text-sm">Crisp, scalable exports that look stunning on everything from a watch to a 4K display.</p>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400 border border-blue-500/20">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a2 2 0 00-1.96 1.414l-.527 1.491a2 2 0 01-.2.274l-2.025 2.025a1.8 1.8 0 01-1.282.527H7.5a.5.5 0 01-.5-.5V14.5a.5.5 0 01.127-.327l.792-.792a1.8 1.8 0 011.282-.527h2.025a2 2 0 01.2.274l.527 1.491a2 2 0 001.96 1.414l2.387-.477a2 2 0 001.022-.547l.527-1.491a2 2 0 01.2-.274z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-white font-semibold mb-1">Developer-Centric Flow</h3>
                                <p className="text-slate-400 text-sm">Effortless batch generation and background removal for professional results.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative group">
                    {/* Decorative Glow */}
                    <div className="absolute -inset-4 bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-700" />

                    <div className="relative glass p-4 rounded-3xl border-white/10">
                        {/* Simple CSS Device Mockup for Home Screen */}
                        <div className="aspect-[9/16] bg-slate-900 rounded-[2.5rem] p-6 overflow-hidden border border-white/10 shadow-2xl">
                            <div className="mb-8 space-y-4">
                                <div className="h-6 w-24 bg-white/5 rounded-full" />
                                <div className="h-4 w-48 bg-white/5 rounded-full" />
                            </div>

                            <div className="grid grid-cols-4 gap-4">
                                {[...Array(12)].map((_, i) => (
                                    <div key={i} className="flex flex-col items-center gap-2">
                                        <div className={`w-full aspect-square rounded-2xl ${i === 2 ? 'bg-gradient-to-tr from-violet-500 to-fuchsia-500 shadow-[0_0_20px_rgba(168,85,247,0.4)]' : 'bg-white/5 animate-pulse'}`}>
                                            {i === 2 && (
                                                <div className="w-full h-full flex items-center justify-center text-white/90">
                                                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M12 2L4.5 20.29L5.21 21L12 18L18.79 21L19.5 20.29L12 2Z" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        <div className="h-1.5 w-8 bg-white/10 rounded-full" />
                                    </div>
                                ))}
                            </div>

                            {/* Dock */}
                            <div className="absolute bottom-6 left-6 right-6 p-4 glass rounded-3xl flex justify-between">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="w-12 h-12 rounded-2xl bg-white/10" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ / Developer Info */}
            <div className="mt-32 pt-16 border-t border-white/5">
                <h2 className="text-2xl font-bold text-white mb-12 text-center">Frequently Asked Questions</h2>
                <div className="grid md:grid-cols-2 gap-x-16 gap-y-12 max-w-5xl mx-auto">
                    <div>
                        <h4 className="text-white font-semibold mb-3">Are the icons App Store ready?</h4>
                        <p className="text-slate-400 text-sm leading-relaxed">
                            Yes. AuraIcon generates icons at 1024x1024 pixels, the standard for App Store Connect.
                            We automatically handle the necessary margins to ensure your icon looks perfect after
                            Apple applies its standard corner radius.
                        </p>
                    </div>
                    <div>
                        <h4 className="text-white font-semibold mb-3">How do I get a transparent background?</h4>
                        <p className="text-slate-400 text-sm leading-relaxed">
                            Simply toggle the "Transparent Background" option before generating. AuraIcon uses
                            advanced AI to remove backgrounds instantly, providing clean WebP and SVG exports
                            with full alpha channel support.
                        </p>
                    </div>
                    <div>
                        <h4 className="text-white font-semibold mb-3">Can I use these icons for Android?</h4>
                        <p className="text-slate-400 text-sm leading-relaxed">
                            Absolutely. While Android supports many shapes (adaptive icons), our high-resolution
                            exports are perfect for use in Android Studio's Image Asset tool to create legacy,
                            round, and squircle icons.
                        </p>
                    </div>
                    <div>
                        <h4 className="text-white font-semibold mb-3">What file formats are supported?</h4>
                        <p className="text-slate-400 text-sm leading-relaxed">
                            We provide high-quality WebP for web/mobile apps and SVG for infinite scalability.
                            Our exports are optimized for small file sizes without compromising on visual
                            fidelity.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
